import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'

interface ScoreRingProps {
  score: number        // 0–100
  label: string
  size?: number
  strokeWidth?: number
  animate?: boolean
}

function scoreColor(score: number): string {
  if (score >= 90) return 'var(--score-good)'
  if (score >= 50) return 'var(--score-average)'
  return 'var(--score-poor)'
}

export default function ScoreRing({
  score,
  label,
  size = 120,
  strokeWidth = 8,
  animate = true,
}: ScoreRingProps) {
  const radius = (size - strokeWidth * 2) / 2
  const circumference = 2 * Math.PI * radius
  const center = size / 2

  const [displayed, setDisplayed] = useState(animate ? 0 : score)
  const color = scoreColor(displayed)
  const offset = circumference - (displayed / 100) * circumference

  useEffect(() => {
    if (!animate) return
    let start: number | null = null
    const duration = 1200

    const step = (timestamp: number) => {
      if (!start) start = timestamp
      const elapsed = timestamp - start
      const progress = Math.min(elapsed / duration, 1)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      setDisplayed(Math.round(eased * score))
      if (progress < 1) requestAnimationFrame(step)
    }

    requestAnimationFrame(step)
  }, [score, animate])

  return (
    <motion.div
      className="flex flex-col items-center gap-2"
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          {/* Track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={strokeWidth}
          />
          {/* Progress */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ transition: 'stroke 0.3s ease, stroke-dashoffset 0.05s linear' }}
          />
        </svg>
        {/* Score number */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span
            className="font-bold tabular-nums"
            style={{ color, fontSize: size * 0.22, lineHeight: 1 }}
          >
            {displayed}
          </span>
        </div>
      </div>
      <span className="text-xs font-medium text-center" style={{ color: 'var(--text-secondary)', maxWidth: size }}>
        {label}
      </span>
    </motion.div>
  )
}
